import { formatDate } from '@angular/common';
import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { FileInputValueModel, InputItemModel, InputValueType, SelectOptionModel, SelectTreeOptionModel } from '@libs/form/models';
import { CheckboxesOptionModel } from '@libs/shared/models';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class ValueFormatterService {
  private readonly passwordValueMessage: string = 'Passwort wurde erstellt oder angepasst';
  private readonly dateOutputFormat: string = 'dd.MM.yyyy';
  private readonly localeId: string = inject(LOCALE_ID);

  public isEmptyValue(item: InputItemModel, value?: InputValueType): boolean {
    return !this.formatValue(item, value)?.length;
  }

  public formatValue(item: InputItemModel, value?: InputValueType): string[] | undefined {
    let result: string | string [] | undefined;

    switch (item.type) {
      case 'autocomplete':
      case 'select':
      case 'radioButtons':
        result = this.formatOptionValue(value, item.options);

        break;
      case 'password':
      case 'passwordCreate':
        result = this.formatPasswordValue(value);

        break;
      case 'date':
        result = this.formatDateValue(value);

        break;
      case 'dateRange':
        result = this.formatDateRangeValue(value);

        break;
      case 'checkbox':
        result = this.formatCheckboxValue(value);

        break;
      case 'checkboxes':
        result = this.formatCheckboxListValue(value, item.options);

        break;
      case 'file':
        result = this.formatFileValue(value);

        break;
      case 'selectTree':
        result = this.formatSelectTreeValue(value, item.options);

        break;
      default:
        result = this.formatPlainValue(value);
    }

    if (!result) {
      return undefined;
    }

    return Array.isArray(result) ? result : [result];
  }

  private formatPlainValue(value: InputValueType): string | undefined {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return value && value !== '' ? String(value) : undefined;
  }

  private formatPasswordValue(value: InputValueType): string | undefined {
    return value ? this.passwordValueMessage : undefined;
  }

  private formatOptionValue(value: InputValueType, options: SelectOptionModel[]): string | undefined {
    if (!value) {
      return undefined;
    }

    const selectedOption: SelectOptionModel | undefined = options.find(
      (option: SelectOptionModel): boolean => option.value === value,
    );

    return selectedOption?.label ?? value as string;
  }

  private formatDateValue(value: InputValueType): string | undefined {
    if (!value) {
      return undefined;
    }

    if (typeof value === 'string') {
      return formatDate(value, this.dateOutputFormat, this.localeId);
    } else if (value instanceof DateTime) {
      return value.toFormat(this.dateOutputFormat, {
        locale: this.localeId,
      });
    }

    return undefined;
  }

  private formatDateRangeValue(value: unknown): string[] | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const dateRangeValue: { start?: DateTime | null; end?: DateTime | null } = value;

    const hasValidStart: boolean = Boolean(dateRangeValue.start?.isValid);
    const hasValidEnd: boolean = Boolean(dateRangeValue.end?.isValid);

    if (!hasValidStart && !hasValidEnd) {
      return undefined;
    }

    const result: string[] = [];

    if (hasValidStart && dateRangeValue.start) {
      result.push(dateRangeValue.start.toFormat(this.dateOutputFormat, { locale: this.localeId }));
    }

    if (hasValidEnd && dateRangeValue.end) {
      result.push(dateRangeValue.end.toFormat(this.dateOutputFormat, { locale: this.localeId }));
    }

    return result;
  }

  private formatCheckboxValue(value: InputValueType): string {
    return value === true ? 'Ja' : 'Nein';
  }

  private formatCheckboxListValue(value: InputValueType, options: CheckboxesOptionModel[]): string[] | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const result: string[] = options
      .filter(option => Boolean((value as Record<string, boolean>)[option.name]))
      .map(option => option.label);

    return result.length > 0 ? result : undefined;
  }

  private formatFileValue(value: InputValueType): string[] | undefined {
    if (!Array.isArray(value)) {
      return undefined;
    }

    return (value as FileInputValueModel[])
      .map(input => input.file.name);
  }

  private formatSelectTreeValue(value: InputValueType, options: SelectTreeOptionModel[]): string[] | undefined {
    if (!value) {
      return undefined;
    }

    const selectedOptions: string[] = Array.from(value as string[]);
    const result: string[] = [];
    let currentOptions: SelectTreeOptionModel[] | undefined = options;

    for (const selectedOption of selectedOptions) {
      const option: SelectTreeOptionModel | undefined = currentOptions?.find(o => o.value === selectedOption);

      if (!option) {
        break;
      }

      result.push(option.label);
      currentOptions = option.options;
    }

    return result;
  }
}
